import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError, API } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { toast } from "sonner";
import {
  MagnifyingGlass, Waveform, LockKey, Clock, Sparkle, Trash, X,
  CheckCircle, VideoCamera, Phone,
} from "@phosphor-icons/react";

export default function Recordings() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [playUrl, setPlayUrl] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const { data } = useQuery({
    queryKey: ["recordings", q],
    queryFn: async () => (await api.get(`/recordings${q ? `?q=${encodeURIComponent(q)}` : ""}`)).data,
  });
  const recs = data?.recordings || [];

  const transcribe = async (r) => {
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/recordings/${r.id}/transcribe`);
      toast.success("Transcript ready");
      qc.invalidateQueries({ queryKey: ["recordings"] });
      setOpen(data.recording);
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete recording "${r.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/recordings/${r.id}`);
      toast.success("Recording deleted");
      qc.invalidateQueries({ queryKey: ["recordings"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const fmtDur = (s) => `${Math.floor((s || 0) / 60)}m ${(s || 0) % 60}s`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Recording Vault</h1>
        <p className="mt-2 text-muted-foreground">Encrypted recordings and SI transcripts for every call and meeting.</p>
      </div>

      <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5">
        <MagnifyingGlass size={18} className="text-muted-foreground" />
        <input data-testid="rec-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search transcripts, numbers, summaries…" className="w-full bg-transparent text-sm outline-none" />
      </div>

      {recs.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
          No recordings {q ? "match your search" : "yet — completed calls appear here automatically"}.
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {recs.map((r) => (
          <div key={r.id} data-testid={`rec-${r.id}`} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary">
                {r.source === "meeting" ? <VideoCamera size={20} weight="duotone" /> : <Phone size={20} weight="duotone" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{r.title}</p>
                <p className="truncate font-mono-metric text-xs text-muted-foreground">{r.reference} · {shortDate(r.created_at)}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground"><Waveform size={11} /> {fmtDur(r.duration_seconds)}</span>
                  <span className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground"><LockKey size={11} /> Encrypted</span>
                  <span className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground"><Clock size={11} /> {r.retention_days}d retention</span>
                  {r.transcribed && <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] text-emerald-700"><CheckCircle size={11} weight="fill" /> Transcribed</span>}
                </div>
              </div>
              <button data-testid={`rec-delete-${r.id}`} onClick={() => remove(r)} className="text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
            </div>
            <div className="mt-3 flex gap-2">
              {r.has_media && (
                <button data-testid={`rec-play-${r.id}`} onClick={() => setPlayUrl(`${API}/recordings/${r.id}/media`)}
                  className="flex items-center justify-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium"><VideoCamera size={14} weight="fill" /> Play</button>
              )}
              {r.transcribed ? (
                <button data-testid={`rec-view-${r.id}`} onClick={() => setOpen(r)} className="flex-1 rounded-full border border-border py-2 text-sm font-medium">View transcript</button>
              ) : (
                <button data-testid={`rec-transcribe-${r.id}`} onClick={() => transcribe(r)} disabled={busyId === r.id}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-primary py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                  <Sparkle size={14} weight="fill" /> {busyId === r.id ? "Transcribing…" : "SI Transcribe"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(null)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between">
              <div><h3 className="font-display text-lg font-semibold">{open.title}</h3><p className="font-mono-metric text-xs text-muted-foreground">{open.reference}</p></div>
              <button onClick={() => setOpen(null)}><X size={18} /></button>
            </div>
            {open.summary && (
              <div className="mb-4 rounded-xl bg-accent/50 p-3">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-accent-foreground"><Sparkle size={12} weight="fill" /> SI Summary</p>
                <p className="text-sm">{open.summary}</p>
              </div>
            )}
            {open.action_items?.length > 0 && (
              <div className="mb-4">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Action items</p>
                <ul className="space-y-1">
                  {open.action_items.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle size={16} className="mt-0.5 shrink-0 text-primary" weight="duotone" /> {a}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Transcript</p>
            <pre className="whitespace-pre-wrap rounded-xl bg-secondary p-3 text-sm leading-relaxed">{open.transcript}</pre>
          </div>
        </div>
      )}
      {playUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPlayUrl(null)} data-testid="rec-player">
          <div className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPlayUrl(null)} className="mb-2 ml-auto block text-white"><X size={20} /></button>
            <video src={playUrl} controls autoPlay className="w-full rounded-2xl bg-black" />
          </div>
        </div>
      )}
    </div>
  );
}
